import { createContext, PropsWithChildren, useContext } from 'react';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { IAgentsListProps } from '@aviv-estimation-funnel/shells/micro-front-end/AgentsList/AgentsList';
import { IProfiCardProps } from '@aviv-estimation-funnel/shells/micro-front-end/ProfiCard/ProfiCard';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import { Provider } from '@aviv-unified-frontend/shell';
import type { Theme } from '@gemini/tokens';
import { IEstimations } from '@mfe-types/context';
import useI18nContext from '@aviv-estimation-funnel/shells/src/app/hooks/useI18nContext';
import { PORTAL_ACRONYMS } from '@mfe-types/portal';
import { getPortalUrl } from '@aviv-estimation-funnel/shells/src/app/helpers/getPortalUrl';
import { BrandProviderProps } from '@aviv-unified-frontend/runtime/brand';

interface ShellProviderProps extends PropsWithChildren {
    mfes: {
        agentsList?:
            | (({ agentsListProps }: { agentsListProps: IAgentsListProps }) => JSX.Element | null)
            | null;
        profiCard?: (({ zipCode, coordinates }: IProfiCardProps) => JSX.Element | null) | null;
        agencyCard?: ({ estimations }: { estimations: IEstimations }) => JSX.Element | null;
        soldPropertiesCard?: ({ estimations }: { estimations: IEstimations }) => JSX.Element | null;
    };
    locale: TLocale;
    animations?: {
        apartment: Record<string, any>;
        house: Record<string, any>;
        individual: Record<string, any>;
        professional: Record<string, any>;
        twoFacades: Record<string, any>;
        threeFacades: Record<string, any>;
        fourFacades: Record<string, any>;
        blockedView: Record<string, any>;
        clearView: Record<string, any>;
        outstandingView: Record<string, any>;
        shield?: Record<string, any>;
        calculationComplete: Record<string, any>;
        calculationProgress: Record<string, any>;
        confettis: Record<string, any>;
    };
}
interface ShellProps extends PropsWithChildren {
    mfes: ShellProviderProps['mfes'];
    theme: Theme;
    locale: TLocale;
    portal: Portal;
    animations?: ShellProviderProps['animations'];
}

const initialMFEDiscoveryContext = {
    mfes: { agentsList: null, profiCard: null },
    locale: 'en' as TLocale,
};

const MFEDiscoveryContext = createContext<ShellProviderProps>(initialMFEDiscoveryContext);

const MFEDiscoveryProvider = (props: ShellProviderProps) => {
    return (
        <MFEDiscoveryContext.Provider value={props}>{props.children}</MFEDiscoveryContext.Provider>
    );
};

const useShellContext = () => useContext(MFEDiscoveryContext);

const ShellProvider = ({ mfes, theme, locale, animations, portal, children }: ShellProps) => {
    const { loading, I18N_CONTEXT } = useI18nContext(locale);

    if (loading) {
        return null;
    }

    return (
        <MFEDiscoveryProvider mfes={mfes} locale={locale} animations={animations}>
            <Provider
                brandContext={{
                    id: PORTAL_ACRONYMS[
                        portal
                    ].toLowerCase() as BrandProviderProps['context']['id'],
                    origin: getPortalUrl(portal),
                }}
                theme={theme}
                i18nContext={I18N_CONTEXT}
            >
                {children}
            </Provider>
        </MFEDiscoveryProvider>
    );
};

export type { ShellProps, ShellProviderProps };
export { ShellProvider, useShellContext };
